import { Tab, Tabs } from "@mui/material";
import { useEffect, useState } from "react";
import { useApiQuery } from "../../services/api";
import biocodexPrivacy from "../../assets/markdowns/organizations/biocodex/privacy.md";
import biocodexTerms from "../../assets/markdowns/organizations/biocodex/terms.md";
import biomerieuxPrivacyEn from "../../assets/markdowns/organizations/biomerieux/privacy-en.md";
import biomerieuxPrivacyEs from "../../assets/markdowns/organizations/biomerieux/privacy-es.md";
import biomerieuxPrivacyFr from "../../assets/markdowns/organizations/biomerieux/privacy-fr.md";
import biomerieuxPrivacyPt from "../../assets/markdowns/organizations/biomerieux/privacy-pt.md";
import juisciPrivacyEn from "../../../../../content/legal/privacy-en.md";
import juisciPrivacyEs from "../../../../../content/legal/privacy-es.md";
import juisciPrivacyFr from "../../../../../content/legal/privacy-fr.md";
import juisciTermsEn from "../../../../../content/legal/terms-en.md";
import juisciTermsEs from "../../../../../content/legal/terms-es.md";
import juisciTermsFr from "../../../../../content/legal/terms-fr.md";
import takedaPrivacyEn from "../../assets/markdowns/organizations/takeda/privacy-en.md";
import takedaPrivacyFr from "../../assets/markdowns/organizations/takeda/privacy-fr.md";
import takedaTermsEn from "../../assets/markdowns/organizations/takeda/terms-en.md";
import takedaTermsFr from "../../assets/markdowns/organizations/takeda/terms-fr.md";
import { displayToast } from "../../components/app/AppToast";
import {
  HeaderBoxAutoSafe,
  HeaderLinks,
  NavBack,
  NavLang,
} from "../../components/app/headers/layout";
import ContentMarkdown from "../../components/ContentMarkdown";
import Loader from "../../components/Loader";
import { PolicyTextStyle } from "../../components/PolicyTextStyle";
import { RoomHeader, RoomPageWrapper } from "../../components/room/layout";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { loadMarkdown } from "../../domains/app/utils/loadMarkdown";
import { useRoomOrganization } from "../../domains/room/hooks/useRoomOrganization";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";

export default function RoomTermsPrivacyPage({ roomId }: { roomId: string }) {
  const roomQuery = useApiQuery("/webapp/private/rooms/:id", {
    param: { id: roomId },
  });
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const { isBiocodexRoom, isTakedaRoom, isBiomerieuxRoom } =
    useRoomOrganization(null);
  const [termsContent, setTermsContent] = useState<string | null>(null);
  const [privacyContent, setPrivacyContent] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (roomId) {
      scrollTo(0, 0);
      void loadTexts();
    }
  }, [roomId, activeLang]);

  async function loadTexts() {
    try {
      let terms: string | undefined;
      let privacy: string | undefined;

      if (isBiocodexRoom) {
        terms = await loadMarkdown(biocodexTerms);
        privacy = await loadMarkdown(biocodexPrivacy);
      } else if (isTakedaRoom) {
        if (activeLang === "fr") {
          terms = await loadMarkdown(takedaTermsFr);
          privacy = await loadMarkdown(takedaPrivacyFr);
        } else {
          terms = await loadMarkdown(takedaTermsEn);
          privacy = await loadMarkdown(takedaPrivacyEn);
        }
      } else if (isBiomerieuxRoom) {
        if (activeLang === "fr") {
          privacy = await loadMarkdown(biomerieuxPrivacyFr);
        } else if (activeLang === "es") {
          privacy = await loadMarkdown(biomerieuxPrivacyEs);
        } else if (activeLang === "pt") {
          privacy = await loadMarkdown(biomerieuxPrivacyPt);
        } else {
          privacy = await loadMarkdown(biomerieuxPrivacyEn);
        }
      }

      if (terms === undefined) {
        if (activeLang === "fr") {
          terms = await loadMarkdown(juisciTermsFr);
        } else if (activeLang === "es") {
          terms = await loadMarkdown(juisciTermsEs);
        } else {
          terms = await loadMarkdown(juisciTermsEn);
        }
      }

      if (privacy === undefined) {
        if (activeLang === "fr") {
          privacy = await loadMarkdown(juisciPrivacyFr);
        } else if (activeLang === "es") {
          privacy = await loadMarkdown(juisciPrivacyEs);
        } else {
          privacy = await loadMarkdown(juisciPrivacyEn);
        }
      }

      setTermsContent(terms);
      setPrivacyContent(privacy);
    } catch (error) {
      console.error("Couldn't load files.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <ScrollableLayout
      header={
        <HeaderBoxAutoSafe>
          <HeaderLinks>
            <NavBack />
          </HeaderLinks>
          {currentRoom && <RoomHeader room={currentRoom} />}
          <HeaderLinks>
            <NavLang />
          </HeaderLinks>
        </HeaderBoxAutoSafe>
      }
    >
      <RoomPageWrapper style={{ backgroundColor: "#fff" }}>
        {termsContent === null || privacyContent === null ? (
          <Loader expanded />
        ) : (
          <>
            <Tabs
              variant="fullWidth"
              centered
              value={activeTab}
              onChange={(_, index: number) => {
                setActiveTab(index);
              }}
              sx={{
                background: "var(--color-secondary-bluegrey-2)",
                "& .MuiButtonBase-root": {
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "120%",
                  letterSpacing: "5e-5em",
                  textTransform: "capitalize",
                  color: "var(--color-secondary-bluegrey-4)",
                  height: "60px",
                  "&.Mui-selected": { color: "var(--color-primary-black)" },
                },
                "& .MuiTabs-indicator": {
                  height: "1.5px",
                  backgroundColor: "var(--color-primary-black)",
                },
              }}
            >
              <Tab label={t("Terms of Use")} />
              <Tab label={t("Privacy Policy")} />
            </Tabs>
            <div
              style={{
                padding: "0px 30px calc(30px + var(--safe-area-bottom))",
              }}
            >
              {activeTab === 0 && (
                <PolicyTextStyle>
                  <ContentMarkdown math={false} linkVariant="anchor">
                    {termsContent}
                  </ContentMarkdown>
                </PolicyTextStyle>
              )}

              {activeTab === 1 && (
                <PolicyTextStyle>
                  <ContentMarkdown math={false} linkVariant="anchor">
                    {privacyContent}
                  </ContentMarkdown>
                </PolicyTextStyle>
              )}
            </div>
          </>
        )}
      </RoomPageWrapper>
    </ScrollableLayout>
  );
}
